import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, authorisationConverter, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import { MdAdd, MdEdit, MdUpload, MdDownload, MdClose, MdCheckCircle } from 'react-icons/md';
import { tidy_date, tidy_date_raw, tidy_time_difference } from '../../utils/helper';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import FileUploadField from '../../components/FileUploadField';
import notify from '../../utils/IANotification';
import { upload_dg_csv } from '../../api/dgAPI';

export default function DiscipleGroupPage() {
  const navigate = useNavigate()
  const { isAuthorised, userRole } = useFetchAuthorisation("user")
  let user_data = useFetchUserData()

  const [dgData, setDGData] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState("")

  // CSV Import State
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)

  useEffect(() => {
    fetchDGData()
  }, [])

  const fetchDGData = async () => {
    setLoading(true)
    let endpoint = config.endpoint + "/dg/"
    let response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      setDGData(data.data || [])
    }
    setLoading(false)
  }

  const downloadSampleCsv = () => {
    const sampleRows = [
      "Name,Tagline,Description,Day,Time,Cluster,Language,Tags,Accepting Members",
      "Living Stones,Rooted in Christ building up one another,\"A vibrant community of young adults doing life and scripture together.\",Friday,19:30,Young Adults,en,\"young adults, fellowship\",1",
      "Grace & Peace,Walking together in God's word and prayer,\"Family-centric group meeting bi-weekly for prayer and fellowship.\",Sunday,11:30,Family,en,\"family, prayer\",1",
      "Joshua Generation,Faithful and strong in the Lord,\"Young working adults pursuing God's calling.\",Wednesday,20:00,Adults,en,\"working adults, discipleship\",1",
      "恩典团契,在基督里扎根 互相扶持建立,\"长者及中文信徒查经团契。\",Sunday,10:30,Seniors,zh,\"chinese, bible study\",1"
    ]
    const content = sampleRows.join("\n")
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'sample_disciple_groups.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFileSelected = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        setCsvText(e.target.result || '')
      }
      reader.readAsText(file)
    }
  }

  const handleCsvUpload = async () => {
    if (!csvText.trim()) {
      notify({ type: "error", message: "Please select or paste CSV content first" })
      return
    }

    setCsvUploading(true)
    setCsvResult(null)

    const res = await upload_dg_csv(csvText)
    setCsvUploading(false)

    if (res?.status === 200) {
      setCsvResult({ success: true, message: res.message || "Disciple groups imported successfully!" })
      notify({ type: "success", message: res.message || "Disciple groups imported successfully!" })
      fetchDGData()
      setTimeout(() => {
        setShowCsvModal(false)
        setCsvText('')
        setCsvResult(null)
      }, 1600)
    } else {
      setCsvResult({ success: false, message: res?.message || "Failed to import disciple groups CSV" })
      notify({ type: "error", message: res?.message || "Failed to import disciple groups CSV" })
    }
  }

  const table_columns = [
    {
      header: "Name",
      accessor: "name"
    },
    {
      header: "Tagline",
      accessor: "tagline",
      modifier: (data) => data || "N/A"
    },
    {
      header: "Tags",
      accessor: "tags",
      modifier: (data) => data?.map(tag => `#${tag}`).join(" ") || "N/A"
    },
    {
      header: "Last Updated",
      accessor: "updatedAt",
      modifier: (data) => `${tidy_date(data)} (${tidy_time_difference(data)})`
    }
  ]

  return (
    <AdminLayout
      navSelected={"Disciple Groups"}
      toolbar={
        <>
          <h1 className="text-lg font-semibold text-gray-900">Disciple Groups</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={MdUpload}
              onClick={() => {
                setCsvText('')
                setCsvResult(null)
                setShowCsvModal(true)
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                navigate("/admin/dg/details?dg_id=new")
              }}
            >
              New Disciple Group
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex-row items-center flex justifyCenter w-100 p-12">
          <h1>Loading...</h1>
        </div>
      ) : (
        <div className="w-100">
          <DataTable
            columns={table_columns}
            data={dgData}
            enableActions
            actions={(row) => {
              return (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" icon={MdEdit} onClick={() => navigate(`/admin/dg/details?dg_id=${row.id}`)}>
                    Edit
                  </Button>
                </div>
              )
            }}
          />
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4 max-h-[92vh] overflow-y-auto">
            <button
              onClick={() => setShowCsvModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <MdClose size={22} />
            </button>

            <div className="flex items-center justify-between pr-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Disciple Groups via CSV</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Upload a CSV file or paste content below. Headers supported: <code className="bg-gray-100 px-1 rounded text-gray-800 font-mono">Name, Tagline, Description, Day, Time, Cluster, Language, Tags, Accepting Members</code>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={MdDownload}
                onClick={downloadSampleCsv}
              >
                Sample CSV
              </Button>
            </div>

            {/* Informational Upsert Notice */}
            <div className="p-3 bg-blue-50/70 rounded-xl border border-blue-100 text-xs text-blue-800 flex items-start gap-2">
              <span className="font-semibold shrink-0">Automatic Upsert:</span>
              <span>The system will automatically update existing disciple groups (matched by Name) or add them as new groups if they do not exist.</span>
            </div>

            {/* File Upload Component */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <FileUploadField
                item_index="dg_csv"
                max_file_size={5}
                max_files={1}
                text="Select or Drag Disciple Groups CSV File"
                subtext="Supported format: .csv"
                onChange={(files) => handleFileSelected(files)}
              />
            </div>

            {/* Text Preview / Edit Area */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CSV Content Preview / Manual Edit:
              </label>
              <textarea
                rows={6}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`Name,Tagline,Description,Day,Time,Cluster,Language,Tags,Accepting Members\nLiving Stones,Rooted in Christ,"Youth & Young adults group",Friday,19:30,Young Adults,en,"fellowship, study",1`}
                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {csvResult && (
              <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                <MdCheckCircle size={18} />
                {csvResult.message}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                icon={MdUpload}
                onClick={handleCsvUpload}
                disabled={csvUploading || !csvText.trim()}
              >
                {csvUploading ? 'Processing...' : 'Process & Import Groups'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
